import * as React from "react";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import { logService } from "npo-common";
import { encodeGetParams } from "src/common/utilities/Utilities";
import { IApplicationState } from "src/state";
import { loadedAgentVerification } from "src/state/agent-verification/actions";
import GreenId from "./GreenId";

interface IPropsFromState {
  sessionId: string;
  verificationProfileUrl: string;
  verificationTagsJsUrl: string;
  verificationIsEnabled: boolean;
  rtfpEnabled: boolean;
  hasConsent: boolean;
  isAuthenticated: boolean;
}

interface IPropsFromDispatch {
  loadedAgentVerification: typeof loadedAgentVerification;
}

type IAgentVerificationProps = IPropsFromState & IPropsFromDispatch;
class AgentVerification extends React.PureComponent<IAgentVerificationProps> {
  private greenIdUrl!: string;
  private sentinelLoaded = false;
  private rtfpLoaded = false;

  public componentDidMount() {
    const { sessionId } = this.props;

    if (sessionId) {
      this.greenIdUrl = `${process.env.REACT_APP_GREEN_ID_TAGS_URL}?${encodeGetParams({
        session_id: this.props.sessionId
      })}`;
    }

    const {
      verificationIsEnabled,
      verificationTagsJsUrl,
      hasConsent,
      loadedAgentVerification,
      isAuthenticated,
      rtfpEnabled
    } = this.props;

    const scriptId = "sentinel";

    if (
      document.getElementById(scriptId) === null &&
      typeof sessionId !== "undefined" &&
      verificationIsEnabled &&
      this.isWcpCookieConsentedOrAuthenticated(hasConsent, isAuthenticated)
    ) {
      const js = document.createElement("script");
      js.setAttribute("id", scriptId);
      js.setAttribute("src", verificationTagsJsUrl);
      document.body.appendChild(js);

      js.onerror = e => {
        logService.logError("AgentVerification: Failed to load tags.js", { e, sessionId });
        if (!rtfpEnabled) {
          loadedAgentVerification(true);
        } else {
          this.sentinelLoaded = true;
          this.checkBothScriptsLoaded();
        }
      };

      js.onload = () => {
        if (!rtfpEnabled) {
          loadedAgentVerification(true);
        } else {
          this.sentinelLoaded = true;
          this.checkBothScriptsLoaded();
        }
      };

      // Add RTFP mdt.js script for fingerprinting
      const rtfpScriptId = "rtfp-mdt";
      if (rtfpEnabled && document.getElementById(rtfpScriptId) === null) {
        
        const rtfpScript = document.createElement("script");
        rtfpScript.setAttribute("id", rtfpScriptId);
        rtfpScript.setAttribute(
          "src",
          `https://fpt.dfp.microsoft.com/mdt.js?session_id=${sessionId}&instanceId=${process.env.REACT_APP_RTFP_ENVIRONMENT_ID}`
        );
        document.body.appendChild(rtfpScript);

        rtfpScript.onload = () => {
          (window as any).dfp.doFpt(document);
          this.rtfpLoaded = true;
          this.checkBothScriptsLoaded();
        };
        rtfpScript.onerror = e => {
          logService.logError("AgentVerification: Failed to load mdt.js", { e, sessionId });
          this.rtfpLoaded = true;
          this.checkBothScriptsLoaded();
        };
      }
    }
  }

  private checkBothScriptsLoaded() {
    if (this.sentinelLoaded && this.rtfpLoaded) {
      this.props.loadedAgentVerification(true);
    }
  }

  public render() {
    return <>{this.greenIdUrl && <GreenId url={this.greenIdUrl} />}</>;
  }

  private isWcpCookieConsentedOrAuthenticated(hasConsent: boolean, isAuthenticated: boolean) {
    if (!hasConsent) {
      return isAuthenticated;
    }

    return hasConsent;
  }
}

const mapStateToProps = ({
  agentVerification,
  wcpCookieConsent: wcpCookieConsent,
  auth,
  flags
}: IApplicationState): IPropsFromState => ({
  ...agentVerification,
  rtfpEnabled: flags.RtfpEnabled,
  hasConsent: wcpCookieConsent.hasConsent,
  isAuthenticated: auth.isAuthenticated
});

const mapDispatchToProps = (dispatch: Dispatch): IPropsFromDispatch => ({
  loadedAgentVerification: (loadedSuccessfully: boolean) => dispatch(loadedAgentVerification(loadedSuccessfully))
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(AgentVerification);
